import { useCallback, useMemo, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import type { MaintenanceProfile, Milestone } from "@/lib/maintenance";
import {
  linksForMilestone,
  linksForService,
  type ServiceLink,
} from "@/lib/maintenance-links";

const CATEGORY_COLORS: Record<string, string> = {
  Engine: "bg-primary",
  Fluids: "bg-chart-2",
  "Tires & Brakes": "bg-chart-3",
  Filters: "bg-chart-4",
  Electrical: "bg-chart-5",
  Drivetrain: "bg-chart-1",
};

const miles = (n: number) => `${n.toLocaleString()} mi`;
const short = (n: number) => (n >= 1000 ? `${n / 1000}k` : String(n));

interface Props {
  profile: MaintenanceProfile;
  milestones: Milestone[];
  fullName: string;
  /** Current odometer reading, used to highlight the upcoming milestone. */
  odometer?: number;
  /** Services already logged as done, keyed by milestone mileage. */
  completed?: Map<number, Set<string>>;
  /** Called when the owner ticks a service off at a milestone. */
  onToggleService?: (service: string, milestone: number, done: boolean) => void;
  /** Slugs enabling contextual spec links from each milestone. */
  makeSlug?: string;
  modelSlug?: string;
  /** Spec types with verified data, so we only link to pages that exist. */
  availableSpecTypes?: string[];
}

export function MaintenanceTimeline({
  profile,
  milestones,
  fullName,
  odometer = 0,
  completed,
  onToggleService,
  makeSlug,
  modelSlug,
  availableSpecTypes = [],
}: Props) {
  const categoryOf = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of profile.items) map.set(item.service, item.category);
    return map;
  }, [profile]);

  const canLink = Boolean(makeSlug && modelSlug);

  const renderLink = (l: ServiceLink, key: string, className: string, ariaLabel?: string) => {
    if (l.kind === "tool") {
      return (
        <Link
          key={key}
          to="/tools/$slug"
          params={{ slug: l.slug }}
          title={l.why}
          aria-label={ariaLabel}
          className={className}
        >
          {l.label}
        </Link>
      );
    }
    if (l.kind === "model") {
      return (
        <Link
          key={key}
          to="/$make/$model"
          params={{ make: makeSlug!, model: modelSlug! }}
          title={l.why}
          aria-label={ariaLabel}
          className={className}
        >
          {l.label}
        </Link>
      );
    }
    return (
      <Link
        key={key}
        to="/$make/$model/$dataType"
        params={{ make: makeSlug!, model: modelSlug!, dataType: l.dataType }}
        title={l.why}
        aria-label={ariaLabel}
        className={className}
      >
        {l.label}
      </Link>
    );
  };

  const maxServices = useMemo(
    () => Math.max(...milestones.map((m) => m.services.length), 1),
    [milestones],
  );

  const doneAt = (m: Milestone) => {
    const set = completed?.get(m.miles);
    if (!set) return 0;
    return m.services.filter((s) => set.has(s)).length;
  };

  const upcoming = useMemo(
    () =>
      milestones.find(
        (m) => m.miles > odometer && doneAt(m) < m.services.length,
      )?.miles ??
      milestones.find((m) => m.miles > odometer)?.miles ??
      null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [milestones, odometer, completed],
  );

  const [selected, setSelected] = useState<number | null>(null);
  const activeMiles = selected ?? upcoming ?? milestones[0]?.miles ?? null;
  const active = milestones.find((m) => m.miles === activeMiles) ?? null;
  const activeIndex = milestones.findIndex((m) => m.miles === activeMiles);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const baseId = `mt-${fullName.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  const tabId = (m: number) => `${baseId}-tab-${m}`;
  const panelId = `${baseId}-panel`;

  const focusTab = useCallback(
    (index: number) => {
      const clamped = (index + milestones.length) % milestones.length;
      const target = milestones[clamped];
      if (!target) return;
      setSelected(target.miles);
      tabRefs.current[clamped]?.focus();
    },
    [milestones],
  );

  const onTabKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    switch (e.key) {
      case "ArrowRight":
      case "ArrowDown":
        e.preventDefault();
        focusTab(index + 1);
        break;
      case "ArrowLeft":
      case "ArrowUp":
        e.preventDefault();
        focusTab(index - 1);
        break;
      case "Home":
        e.preventDefault();
        focusTab(0);
        break;
      case "End":
        e.preventDefault();
        focusTab(milestones.length - 1);
        break;
      default:
        break;
    }
  };

  const usedCategories = useMemo(() => {
    const set = new Set<string>();
    for (const m of milestones) for (const s of m.services) set.add(categoryOf.get(s) ?? "Engine");
    return [...set];
  }, [milestones, categoryOf]);

  if (!milestones.length) return null;

  return (
    <div>
      <h3 className="font-semibold text-foreground">
        {fullName} maintenance timeline
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Each bar is a mileage milestone; its height is how many services come due there.
        Select a milestone to see exactly what needs doing.
      </p>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2">
        {usedCategories.map((c) => (
          <span key={c} className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span
              className={`h-2.5 w-2.5 rounded-sm ${CATEGORY_COLORS[c] ?? "bg-primary"}`}
              aria-hidden="true"
            />
            {c}
          </span>
        ))}
      </div>

      {/* Chart */}
      <div className="mt-4 overflow-x-auto pb-2">
        <p id={`${baseId}-help`} className="sr-only">
          Use the left and right arrow keys to move between mileage milestones, or Home
          and End for the first and last. The details below update as you move.
        </p>
        <ul
          className="flex min-w-[560px] items-end gap-1.5 sm:gap-2"
          role="tablist"
          aria-orientation="horizontal"
          aria-label={`${fullName} service milestones by mileage`}
          aria-describedby={`${baseId}-help`}
        >
          {milestones.map((m, index) => {
            const isActive = m.miles === activeMiles;
            const isPast = odometer > 0 && m.miles <= odometer;
            const doneCount = doneAt(m);
            const allDone = doneCount > 0 && doneCount === m.services.length;
            const barHeight = 24 + Math.round((m.services.length / maxServices) * 116);
            return (
              <li
                key={m.miles}
                role="presentation"
                className="flex h-full flex-1 flex-col items-center justify-end gap-1"
              >
                <span
                  aria-hidden="true"
                  className={`text-[11px] font-medium tabular-nums ${
                    allDone ? "text-primary" : isActive ? "text-primary" : "text-muted-foreground"
                  }`}
                >
                  {allDone ? "✓" : doneCount > 0 ? `${doneCount}/${m.services.length}` : m.services.length}
                </span>
                <button
                  type="button"
                  ref={(el) => {
                    tabRefs.current[index] = el;
                  }}
                  role="tab"
                  id={tabId(m.miles)}
                  aria-controls={panelId}
                  aria-selected={isActive}
                  tabIndex={index === (activeIndex === -1 ? 0 : activeIndex) ? 0 : -1}
                  onKeyDown={(e) => onTabKeyDown(e, index)}
                  onClick={() => setSelected(m.miles)}
                  onMouseEnter={() => setSelected(m.miles)}
                  title={`${miles(m.miles)}: ${m.services.join(", ")}${
                    allDone ? " (all logged as done)" : ""
                  }`}
                  className={`flex w-full flex-col-reverse overflow-hidden rounded-t-sm transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
                    isActive
                      ? "ring-2 ring-primary ring-offset-1 ring-offset-background"
                      : isPast || allDone
                        ? "opacity-40"
                        : "opacity-85 hover:opacity-100"
                  }`}
                  style={{ height: barHeight }}
                >
                  <span className="sr-only">
                    {miles(m.miles)} milestone, {m.services.length} service
                    {m.services.length === 1 ? "" : "s"} due: {m.services.join(", ")}
                    {doneCount > 0 && !allDone
                      ? ` — ${doneCount} of ${m.services.length} logged as done`
                      : ""}
                    {allDone ? " — all logged as done" : ""}
                    {isPast ? " — already behind your current mileage" : ""}
                  </span>
                  {m.services.map((s) => {
                    const isDone = completed?.get(m.miles)?.has(s) ?? false;
                    return (
                      <span
                        key={s}
                        aria-hidden="true"
                        className={`w-full flex-1 border-b border-background/60 ${
                          isDone
                            ? "bg-muted"
                            : (CATEGORY_COLORS[categoryOf.get(s) ?? "Engine"] ?? "bg-primary")
                        }`}
                      />
                    );
                  })}
                </button>
                <span
                  aria-hidden="true"
                  className={`text-[10px] tabular-nums ${
                    isActive ? "font-semibold text-foreground" : "text-muted-foreground"
                  }`}
                >
                  {short(m.miles)}
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      {odometer > 0 && upcoming ? (
        <p className="mt-1 text-xs text-muted-foreground">
          Faded bars are already behind you at {miles(odometer)}. Your next milestone is{" "}
          {miles(upcoming)}.
        </p>
      ) : null}

      {/* Selected milestone detail */}
      {active ? (
        <div
          id={panelId}
          role="tabpanel"
          tabIndex={0}
          aria-labelledby={tabId(active.miles)}
          aria-live="polite"
          className="mt-4 rounded-lg border border-border bg-secondary/20 p-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h4 className="font-heading text-lg font-bold text-primary">
              {miles(active.miles)} service
            </h4>
            <span className="text-xs text-muted-foreground">
              {active.services.length} item{active.services.length === 1 ? "" : "s"} due
            </span>
          </div>
          <ul className="mt-3 space-y-2">
            {active.services.map((s) => {
              const item = profile.items.find((i) => i.service === s);
              const isDone = completed?.get(active.miles)?.has(s) ?? false;
              return (
                <li key={s} className="flex gap-2 text-sm">
                  {onToggleService ? (
                    <input
                      type="checkbox"
                      checked={isDone}
                      onChange={(e) => onToggleService(s, active.miles, e.target.checked)}
                      aria-label={`Mark ${s} at ${miles(active.miles)} as completed`}
                      className="mt-1 h-4 w-4 shrink-0 accent-primary"
                    />
                  ) : (
                    <span
                      className={`mt-1.5 h-2 w-2 shrink-0 rounded-sm ${
                        CATEGORY_COLORS[categoryOf.get(s) ?? "Engine"] ?? "bg-primary"
                      }`}
                      aria-hidden="true"
                    />
                  )}
                  <span className={isDone ? "line-through opacity-60" : undefined}>
                    <span className="font-medium text-foreground">{s}</span>
                    {item ? (
                      <span className="text-muted-foreground"> — {item.note}</span>
                    ) : null}
                    {canLink ? (
                      <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                        <span className="text-xs text-muted-foreground" id={`${baseId}-specs-${active.miles}`}>
                          Specs you need:
                        </span>
                        {linksForService(s, categoryOf.get(s) ?? "Engine", {
                          availableSpecTypes,
                        }).map((l, i) =>
                          renderLink(
                            l,
                            `${s}-${i}`,
                            "text-xs font-medium text-primary underline decoration-primary/40 underline-offset-2 hover:decoration-primary",
                            `${l.label} for ${s}`,
                          ),
                        )}
                      </span>
                    ) : null}
                  </span>
                </li>
              );
            })}
          </ul>
          {canLink ? (
            <div className="mt-4 border-t border-border/60 pt-3">
              <p className="text-xs font-medium text-foreground" id={`${baseId}-check-${active.miles}`}>
                Before the {miles(active.miles)} service, check:
              </p>
              <div
                className="mt-2 flex flex-wrap gap-2"
                role="group"
                aria-labelledby={`${baseId}-check-${active.miles}`}
              >
                {linksForMilestone(
                  active.services,
                  (s) => categoryOf.get(s) ?? "Engine",
                  { availableSpecTypes },
                ).map((l, i) =>
                  renderLink(
                    l,
                    `ms-${active.miles}-${i}`,
                    "rounded-full border border-border bg-background px-3 py-1 text-xs font-medium text-foreground hover:border-primary hover:text-primary",
                    `${l.label} — before the ${miles(active.miles)} service`,
                  ),
                )}
              </div>
            </div>
          ) : null}
          {onToggleService ? (
            <p className="mt-3 text-xs text-muted-foreground">
              Tick a service to add it to your maintenance log — it's saved on this
              device only.
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
